import {
  Component, EventEmitter, Input, OnInit, Output
} from '@angular/core';
import { type UntypedFormGroup, AbstractControl } from '@angular/forms';

import { getPdfPageCount } from 'app/_api/pdf/get-pdf-page-count';
import { loadPdf } from 'app/_api/pdf/load-pdf';
import { type UploadPhotoMod } from 'models/layout-mod.model';
import { type MediaResourceType } from 'models/media.model';
import { StylingService } from 'shared/services/styling.service';

@Component({
  selector: 'app-input-file',
  templateUrl: './input-file.component.html',
  styleUrls: ['./input-file.component.scss']
})
export class InputFileComponent implements OnInit {
  readonly validImageMimeTypes: string[] = ['image/jpg', 'image/png'];

  readonly validPdfMimeTypes = ['application/pdf'];

  @Input() mainForm!: UntypedFormGroup;

  @Input() controlName!: string;

  @Input() resourceType: MediaResourceType = 'Account';

  @Input() layoutMod: UploadPhotoMod[] = ['circle'];

  imgSrc = '';

  @Input() set imageSrc(src: string) {
    this.imgSrc = '';
    this.fileImageSrc = '';
    this.loadImage(src, () => { this.imgSrc = src; });
  }

  @Input() defaultImageSrc = '';

  @Input() buttonText = '';

  // This is used if you have multiple uploads on same page
  @Input() uploadId = 'uploadPhoto';

  @Input() disabled = false;

  @Output() openPreview = new EventEmitter();

  control!: AbstractControl | null;

  layoutModifier = '';

  showErrorMessage = false;

  maxWidth = 0;

  maxHeight = 0;

  fileImageSrc = '';

  mimeType = '';

  /** Maximum file size in bytes. */
  maxFileSize = Infinity;

  pdfErrorType = undefined as ('NOT_A_PDF' | 'PDF_SINGLE_PAGE' | 'PDF_SIZE' | undefined);

  constructor(
    private stylingService: StylingService
  ) {
    //
  }

  ngOnInit(): void {
    this.init();
  }

  loadImage(url: string, onLoad: () => void): void {
    const img = new Image();
    img.src = url;
    img.onload = onLoad;
  }

  private getMimeTypes(): string[] {
    switch (this.resourceType) {
      case 'Account':
      case 'Branch':
        return this.validImageMimeTypes;

      case 'PDF_FIRST_PAGE':
      case 'PDF_FOLLOWING_PAGES':
        return this.validPdfMimeTypes;

      default:
        ((_: never) => {
          // exhaustive check
        })(this.resourceType);

        return [];
    }
  }

  private init(): void {
    this.setValidators();
    this.control = this.mainForm.get(this.controlName);
    this.layoutModifier = this.stylingService.concatLayoutModifiers('inputFile', this.layoutMod);
    this.mimeType = this.getMimeTypes().join(', ');
  }

  private setValidators(): void {
    switch (this.resourceType) {
      case 'Account':
      case 'Branch':
        this.maxWidth = 300;
        this.maxHeight = 300;
        break;

      case 'PDF_FIRST_PAGE':
      case 'PDF_FOLLOWING_PAGES':
        this.maxFileSize = 1048576; // 1 MB * 1024 * 1024
        break;

      default:
        ((_: never) => {
          // exhaustive check
        })(this.resourceType);
        break;
    }
  }

  validateImageDimensions(image: HTMLImageElement): boolean {
    this.showErrorMessage = false;

    let maxWidth: number;
    let maxHeight: number;

    switch (this.resourceType) {
      case 'Account':
      case 'Branch':
      default:
        maxWidth = 300;
        maxHeight = 300;
        break;
    }

    if (image.width > maxWidth || image.height > maxHeight) {
      this.showErrorMessage = true;

      return false;
    }

    return true;
  }

  uploadImage(file?: File): void {
    if (file) {
      if (file.type && !this.getMimeTypes().includes(file.type)) {
        return;
      }

      const reader = new FileReader();

      reader.readAsDataURL(file);

      reader.onload = (e: any): void => {
        const image = new Image();

        image.src = e.target.result;
        image.onload = (): void => {
          if (this.validateImageDimensions(image)) {
            this.mainForm.controls[this.controlName].setValue(file);
            this.fileImageSrc = image.src;
          } else {
            this.mainForm.controls[this.controlName].setErrors({ invalidImageDimensions: true });
          }
        };
      };
    }
  }

  uploadPdf(file?: File): void {
    if (file) {
      if (file.type && !this.getMimeTypes().includes(file.type)) {
        this.pdfErrorType = 'NOT_A_PDF';
        this.mainForm.controls[this.controlName].setErrors({ notAPdf: true });

        return;
      }

      if (typeof file.size === 'number' && (file.size / 1024) > this.maxFileSize) {
        this.pdfErrorType = 'PDF_SIZE';
        this.mainForm.controls[this.controlName].setErrors({ pdfSize: true });

        return;
      }

      const reader: FileReader = new FileReader();

      reader.readAsArrayBuffer(file);

      reader.onload = async (): Promise<void> => {
        if (reader.result) {
          const buffer = typeof reader.result === 'string'
            ? (new TextEncoder()).encode(reader.result)
            : reader.result;

          if (buffer.byteLength > this.maxFileSize) {
            this.pdfErrorType = 'PDF_SIZE';
            this.mainForm.controls[this.controlName].setErrors({ pdfSize: true });

            return;
          }

          try {
            const pdf = await loadPdf(buffer);
            if (!pdf) {
              this.pdfErrorType = 'NOT_A_PDF';
              this.mainForm.controls[this.controlName].setErrors({ notAPdf: true });

              return;
            }

            const pageCount = getPdfPageCount(pdf);
            if (pageCount !== 1) {
              this.pdfErrorType = 'PDF_SINGLE_PAGE';
              this.mainForm.controls[this.controlName].setErrors({ pdfSinglePage: true });

              return;
            }

            this.mainForm.controls[this.controlName].setValue([file, pdf]);
            this.resetInputFileControl();
          } catch (e) {
            console.log(e instanceof Error ? e.message : e);
            this.pdfErrorType = 'NOT_A_PDF';
            this.mainForm.controls[this.controlName].setErrors({ notAPdf: true });
          }
        }
      };
    }
  }

  upload(event: Event): void {
    const input: HTMLInputElement = (event.target as HTMLInputElement);

    if (input && input.files && input.files.length >= 1) {
      const file: File = input.files[0];

      switch (this.resourceType) {
        case 'Account':
        case 'Branch':
          this.uploadImage(file);
          break;

        case 'PDF_FIRST_PAGE':
        case 'PDF_FOLLOWING_PAGES':
          this.uploadPdf(file);
          break;

        default:
          ((_: never) => {
            // exhaustive check
          })(this.resourceType);
          break;
      }
    }
  }

  resetInputFileControl(): void {
    const inputElement = document.querySelector(`input[type='file'][id='${this.uploadId}']`) as HTMLInputElement;
    if (inputElement) {
      inputElement.value = '';
    }
  }

  onOpenPreview(): void {
    this.openPreview.emit();
  }
}
